import { CommonModule, DatePipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { AvatarComponent } from '@mds/ui';
import { EventDelegatorService } from '@services/event-delegator.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MakersiteService } from '@services/makersite.service';
import { finalize, Subject } from 'rxjs';

@Component({
  selector: 'app-compliance-questionnaire-comments',
  templateUrl: './compliance-questionnaire-comments.component.html',
  styleUrls: ['./compliance-questionnaire-comments.component.scss'],
  imports: [CommonModule, AvatarComponent, DatePipe, ReactiveFormsModule, MdsIconsModule],
  standalone: true
})
export class ComplianceQuestionnaireCommentsComponent implements OnInit, OnDestroy {
  @Output() commentChanged = new EventEmitter<string>();
  @Output() commentSubmit = new EventEmitter<boolean>();
  icons = MdsIcon;
  router = inject(Router);
  localStorageService = inject(LocalStorageService);
  commentsList = [];
  commentForm: UntypedFormGroup = new UntypedFormGroup({});
  @Input() data?: any;
  @Input() showSendOption = true;
  threadId = null;
  questionnaireId = this.router.url.split('/request/')[1];
  userInfo = this.localStorageService.getItem('user_info');
  private destroy$ = new Subject<void>();
  private isSubmitting = false;

  constructor(
    private fb: UntypedFormBuilder,
    private makersiteService: MakersiteService,
    private eventDelegatorService: EventDelegatorService
  ) {
    this.commentForm = this.fb.group({
      comment: ['', [Validators.required]]
    });
  }
  ngOnInit(): void {
    if (this.data?.thread) {
      this.threadId = this.data.thread.id;
      this.getCommentList();
    }
    this.commentForm.get('comment').valueChanges.subscribe((value) => {
      this.commentChanged.emit(value?.trim());
    });
  }

  getCommentList() {
    const payload = { limit: 50, offset: 0 };

    this.makersiteService.getMessages(payload, this.threadId).subscribe((response: any) => {
      let commentResponse = response?.messages ?? [];

      if (commentResponse.length > 0) {
        const userMap = Array.isArray(this.data?.thread?.participants)
          ? new Map(this.data.thread.participants.map((user) => [user.user_id, user]))
          : new Map();

        commentResponse = commentResponse.map((comment: any) => {
          const user: any = userMap.get(comment.user_id);

          if (user) {
            comment.firstname = user.first_name;
            comment.lastname = user.last_name;
            comment.user_role = user.user_role;
          } else if (comment.user_id === this.userInfo?.rid) {
            comment.firstname = comment.firstname || this.userInfo.firstname;
            comment.lastname = comment.lastname || this.userInfo.lastname;
          }

          return comment;
        });
      }

      this.commentsList = commentResponse;
    });
  }

  addLinebreak(str: string) {
    if (!str) return '';
    return str
      .replace(/\\r\\n|\\n|\\r/g, '\n')
      .replace(/\\\s*/g, '\n')
      .replace(/\r\n|\r|\n/g, '\n')
      .trim();
  }

  submitComment(textarea: HTMLTextAreaElement) {
    if (!this.commentForm.valid) return;
    textarea.style.height = 'auto';
    const paramsComment = {
      threadId: this.threadId,
      comment: this.commentForm.value.comment,
      selectedProductId: this.data.selectedProducts[0]?.rid,
      selectedRegulationId: this.data?.selectedRsl?.rid,
      questionnaireId: this.questionnaireId
    };
    this.sendComment(paramsComment);
  }

  sendComment(paramsComment?: any) {
    if (paramsComment.threadId) {
      this.addComment(paramsComment);
    } else {
      this.createNewThread(paramsComment);
    }
  }

  createNewThread(paramsComment) {
    const commentContent = paramsComment.comment;
    if (!commentContent || this.isSubmitting) {
      return;
    }
    const payload = {
      message: { content: commentContent },
      tags: [
        `sku:${paramsComment?.selectedProductId}`,
        `regulation:${paramsComment?.selectedRegulationId}`,
        `questionnaire:#${paramsComment.questionnaireId}`
      ]
    };
    this.makersiteService
      .createThread(payload)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response: any) => {
          this.threadId = response?.thread?.id;
          this.commentForm.reset();
          this.getCommentList();
          this.reload();
          this.commentSubmit.emit(true);
        }
      });
  }

  addComment(paramsComment) {
    const commentContent = paramsComment.comment;
    if (!commentContent || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    const payload = { content: commentContent };
    this.makersiteService
      .addComment(payload, this.threadId)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.commentForm.reset();
          this.getCommentList();
          this.reload();
          this.commentSubmit.emit(true);
        }
      });
  }

  onEnter(event: KeyboardEvent, textarea: HTMLTextAreaElement) {
    event.preventDefault();
    this.submitComment(textarea);
  }

  private reload() {
    this.eventDelegatorService.emiter('custom-ngx-event', {
      type: 'toolbar-reload-event',
      action: 'reload',
      controller: window.location.pathname
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
